import "@videojs/react/video/skin.css";
import "./video-player.css";

import { I18nProvider } from "@videojs/react/i18n";
import { Video, VideoPlayer as Player, VideoSkin } from "@videojs/react/video";
import { useState, type CSSProperties, type Ref } from "react";

import { cx } from "@/lib/cx";

export type VideoPlayerProps = {
  /** Leave out when the caller owns `src` through `videoRef`. */
  src?: string;
  poster?: string;
  /** The shape to lay out with until the video reports its own: width ÷ height, or CSS `"9 / 16"`. */
  ratio?: number | string;
  /** The tallest the player may get, as a CSS length. */
  maxHeight: string;
  className?: string;
  autoPlay?: boolean;
  preload?: "none" | "metadata" | "auto";
  videoRef?: Ref<HTMLVideoElement>;
  onError?: () => void;
};

function ratioValue(ratio: number | string): number {
  if (typeof ratio === "number") return ratio;
  const [w, h] = ratio.split("/").map(Number);
  return w && h ? w / h : 16 / 9;
}

/**
 * Every video in the app plays here: Video.js 10 with its default skin, in Thai.
 * It brings what WebKit's bare controls lack — speed, keyboard shortcuts
 * (space, ←/→, j/l, 0–9, `<`/`>`), double-tap seeking, picture-in-picture,
 * AirPlay — and the same controls in every skin.
 *
 * The skin stretches the video to its box, so the box takes the video's own
 * shape once the metadata is in, capped at `maxHeight`, and never letterboxes.
 */
export function VideoPlayer({ src, poster, ratio = 16 / 9, maxHeight, className, autoPlay, preload = "metadata", videoRef, onError }: VideoPlayerProps) {
  const [shape, setShape] = useState(() => ratioValue(ratio));
  const style = { "--vp-ratio": shape, "--vp-max-h": maxHeight } as CSSProperties;
  return (
    <I18nProvider locale="th">
      <Player poster={poster}>
        <VideoSkin className={cx("kobo-video", className)} style={style}>
          <Video
            ref={videoRef}
            src={src}
            autoPlay={autoPlay}
            preload={preload}
            playsInline
            onError={onError}
            onLoadedMetadata={(event) => {
              const { videoWidth, videoHeight } = event.currentTarget;
              if (videoWidth > 0 && videoHeight > 0) setShape(videoWidth / videoHeight);
            }}
          />
        </VideoSkin>
      </Player>
    </I18nProvider>
  );
}
